/**
 * Paste question/answer pairs (CSV or TSV with a header), preview what will be created, then import.
 * Each pair is linked to the article that covers it and gets its scope drafted; every one is checked,
 * and none is served until it is promoted.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useImportKnowledge, usePreviewImport, useSelectedBuId } from '@/hooks/use-knowledge-promotion';
import { getApiErrorMessage } from '@/lib/utils';
import type { ImportPreview } from './types';

const EXAMPLE =
	'question,answer,article,not_when\n"Do Buoy drops contain sugar?","No. Buoy Hydration Drops have no sugar or additives.",Hydration FAQ,"How much sodium is in a serving? | How many drops should I take?"';

interface ImportDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const ImportDialog = ({ open, onOpenChange }: ImportDialogProps) => {
	const [text, setText] = useState('');
	const [preview, setPreview] = useState<ImportPreview | null>(null);
	const previewImport = usePreviewImport();
	const importPairs = useImportKnowledge();
	const buId = useSelectedBuId();

	const close = () => {
		setText('');
		setPreview(null);
		onOpenChange(false);
	};

	const check = async () => {
		try {
			setPreview(await previewImport.mutateAsync({ text }));
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not read the pairs'));
		}
	};

	const start = async () => {
		try {
			const { job } = await importPairs.mutateAsync({ text, buId });
			toast.success(`Importing ${job.total} pairs. They appear under To review as they are checked.`);
			close();
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not import'));
		}
	};

	const tooMany = preview ? preview.rows.length > preview.max : false;

	return (
		<Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
			<DialogContent className='sm:max-w-2xl' data-testid='import-dialog'>
				<DialogHeader>
					<DialogTitle>Import question and answer pairs</DialogTitle>
					<DialogDescription>
						Paste CSV or TSV with a header row. <code>question</code> and <code>answer</code> are required; <code>article</code>,{' '}
						<code>intent</code>, <code>use_when</code> and <code>not_when</code> (several separated by <code>|</code>) are optional.
						Anything missing is drafted for you to review.
					</DialogDescription>
				</DialogHeader>
				<Textarea
					rows={8}
					className='font-mono text-[12px]'
					placeholder={EXAMPLE}
					value={text}
					onChange={(event) => {
						setText(event.target.value);
						setPreview(null);
					}}
				/>
				{preview && (
					<div className='flex max-h-64 flex-col gap-1 overflow-y-auto rounded-md border p-2' data-testid='import-preview'>
						<p className='text-xs font-medium'>
							{preview.rows.length} {preview.rows.length === 1 ? 'pair' : 'pairs'} to import
							{tooMany && <span className='text-red-700'> — at most {preview.max} at a time</span>}
						</p>
						{preview.errors.map((error) => (
							<p key={error} className='text-[11px] text-red-700'>
								{error}
							</p>
						))}
						{preview.rows.slice(0, 50).map((row, index) => (
							<div key={index} className='border-t pt-1 text-[12px]'>
								<p className='font-medium'>{row.question}</p>
								<p className='line-clamp-2 text-muted-foreground'>{row.answer}</p>
								{(row.article || row.notWhen?.length) && (
									<p className='text-[11px] text-muted-foreground'>
										{row.article ? `Article: ${row.article}` : ''}
										{row.article && row.notWhen?.length ? ' · ' : ''}
										{row.notWhen?.length ? `Not when: ${row.notWhen.join('; ')}` : ''}
									</p>
								)}
							</div>
						))}
					</div>
				)}
				<DialogFooter>
					<Button variant='ghost' onClick={close}>
						Cancel
					</Button>
					{preview && preview.rows.length > 0 && !tooMany ? (
						<Button disabled={importPairs.isPending} onClick={start}>
							{importPairs.isPending && <Loader2 className='size-3.5 animate-spin' aria-hidden />}
							Import {preview.rows.length}
						</Button>
					) : (
						<Button disabled={!text.trim() || previewImport.isPending} onClick={check}>
							{previewImport.isPending && <Loader2 className='size-3.5 animate-spin' aria-hidden />}
							Preview
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
